import { useMemo, useState } from "react";
import { parseSource } from "@/api/client";
import type { SourceMeta } from "@/api/types";
import { FileText, ExternalLink, Eye, X, RotateCcw, Clock, AlertTriangle } from "lucide-react";

interface Props {
  sources: string[];
  // 아래 3개가 모두 있을 때만 '제외(deselect) 후 재생성' UI가 활성화된다.
  // (공유·이력 등 읽기전용 화면에서는 미전달 → 기존 표시 전용 동작)
  question?: string;
  customer?: string;
  onRegenerate?: (question: string, excludeUrls: string[], customer?: string) => void;
  // 기능③: 출처 신선도 메타(url 로 매칭). 기능①: 각주 스크롤 대상 id 접두어(있으면 번호칩 표시)
  meta?: SourceMeta[];
  idPrefix?: string;
}

export default function SourceList({ sources, question, customer, onRegenerate, meta, idPrefix }: Props) {
  const [preview, setPreview] = useState<{ title: string; url: string } | null>(null);
  // 제외할 출처 url 집합(체크 해제된 항목)
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  const parsed = useMemo(() => sources.map((s) => parseSource(s)), [sources]);
  // 신선도 메타 url → 정보 매핑
  const metaByUrl = useMemo(() => {
    const m = new Map<string, SourceMeta>();
    (meta ?? []).forEach((x) => x.url && m.set(x.url, x));
    return m;
  }, [meta]);
  if (!sources?.length) return null;

  // url 이 있는 항목만 제외 대상(선택 가능)
  const selectable = parsed.some((p) => !!p.url);
  const interactive = !!onRegenerate && !!question && selectable;
  const keptCount = parsed.filter((p) => !p.url || !excluded.has(p.url)).length;

  const toggle = (url: string) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const runRegenerate = () => {
    if (!interactive || excluded.size === 0 || keptCount < 1) return;
    onRegenerate!(question!, Array.from(excluded), customer);
  };

  return (
    <div className="mt-3 space-y-1.5 border-t border-border pt-2">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-muted-foreground">참고 문서</div>
        {interactive && (
          <span className="text-[11px] text-muted-foreground">
            관련 없는 자료는 체크 해제하세요
          </span>
        )}
      </div>

      {parsed.map((p, i) => {
        const isExcluded = !!p.url && excluded.has(p.url);
        const fresh = p.url ? metaByUrl.get(p.url) : undefined;
        return (
          <div
            key={i}
            id={idPrefix ? `${idPrefix}-${i + 1}` : undefined}
            className={
              "flex items-start gap-1.5 rounded-md px-2 py-1.5 text-xs transition-colors " +
              (isExcluded ? "bg-muted/30 opacity-50" : "bg-muted/60")
            }
          >
            {idPrefix && (
              <span
                className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#EB4E26]/10 text-[10px] font-semibold text-[#EB4E26]"
                title={`근거 ${i + 1}`}
              >
                {i + 1}
              </span>
            )}
            {interactive && p.url ? (
              <input
                type="checkbox"
                checked={!isExcluded}
                onChange={() => toggle(p.url!)}
                className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[#EB4E26]"
                aria-label={isExcluded ? "이 문서 다시 포함" : "이 문서 제외"}
                title={isExcluded ? "이 문서 다시 포함" : "이 문서 제외"}
              />
            ) : (
              !idPrefix && <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            )}
            <span className="min-w-0 flex-1">
              {p.label && <span className="mr-1 text-muted-foreground">[{p.label}]</span>}
              <span className={"break-words " + (isExcluded ? "line-through" : "")}>{p.title}</span>
              {fresh?.updated_date && (
                <span
                  className={
                    "mt-0.5 flex items-center gap-1 text-[10px] " +
                    (fresh.stale ? "text-amber-600" : "text-muted-foreground")
                  }
                  title={fresh.stale ? "오래된 문서 — 최신 여부를 확인하세요" : "마지막 갱신일"}
                >
                  {fresh.stale ? (
                    <AlertTriangle className="h-3 w-3 shrink-0" />
                  ) : (
                    <Clock className="h-3 w-3 shrink-0" />
                  )}
                  <span>
                    {fresh.updated_date} 갱신
                    {fresh.stale ? " · 오래된 문서" : ""}
                  </span>
                </span>
              )}
            </span>
            {p.url && (
              <>
                <button
                  onClick={() => setPreview({ title: p.title, url: p.url! })}
                  className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
                  aria-label="미리보기"
                  title="미리보기"
                >
                  <Eye className="h-3.5 w-3.5" />
                </button>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
                  aria-label="새 탭에서 열기"
                  title="새 탭에서 열기"
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              </>
            )}
          </div>
        );
      })}

      {interactive && excluded.size > 0 && (
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="text-[11px] text-muted-foreground">
            {excluded.size}건 제외 · {keptCount}건 사용
          </span>
          <button
            onClick={runRegenerate}
            disabled={keptCount < 1}
            className="inline-flex items-center gap-1 rounded-md bg-[#EB4E26] px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-40"
            title="선택한 문서만 근거로 다시 답변"
          >
            <RotateCcw className="h-3.5 w-3.5" /> 선택한 문서로 다시 답변
          </button>
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3"
          onClick={() => setPreview(null)}
        >
          <div
            className="flex h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-card shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 border-b border-border px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{preview.title}</span>
              <a
                href={preview.url}
                target="_blank"
                rel="noreferrer"
                className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
              >
                새 탭에서 열기
              </a>
              <button
                onClick={() => setPreview(null)}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted"
                aria-label="닫기"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <iframe
              src={preview.url}
              title={preview.title}
              className="min-h-0 flex-1 bg-background"
            />
            <div className="border-t border-border px-3 py-1.5 text-center text-[11px] text-muted-foreground">
              내용이 보이지 않으면 상단 “새 탭에서 열기”를 이용하세요.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
